import { type JSX, useCallback, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import {
  type Steuerschluessel,
  type Warengruppe,
  type Warengruppenfelder,
  steuerschluesselAlle,
  warengruppeAendern,
  warengruppeAnlegen,
  warengruppeLoeschen,
  warengruppen as holeWarengruppen,
} from '../api/produkte';
import { Dialog } from '../komponenten/Dialog';
import { Meldung } from '../komponenten/Meldung';
import { Liste } from '../komponenten/Liste';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';

/**
 * Warengruppenverwaltung (PRD-02, PRD-04, PRF-11, SYS-10).
 *
 * ============================================================================
 * WARUM DIESE MASKE ENTSTANDEN IST
 * ============================================================================
 *
 * SYS-10: „Die Administration pflegt die als administrativ ausgewiesenen
 * Gegenstände selbst — ohne Programmierung, ohne neue Programmfassung."
 * Warengruppen waren keine Ausnahme, es gab nur keine Maske. Am 09.09.2026
 * standen **null** Gruppen im Bestand, und damit lief die halbe
 * Produktkonfiguration ins Leere:
 *
 *   - **PRD-02** vererbt Steuersatz, Erlöskonto und Standardtext an
 *     Untergruppen und Artikel. Ohne Gruppe gibt es nichts zu erben.
 *   - **PRD-04** verlangt das Erlöskonto je Warengruppe, getrennt nach
 *     Inland, EU und Drittland.
 *   - **PRF-11** verlangt den Höchstrabatt „je Artikel **und**
 *     Warengruppe" — die Spalte gab es seit 0028, erreichbar war sie nicht.
 *   - **FIB-04** ordnet Warengruppe → Produktbuchungsgruppe zu. Die
 *     Zuordnungstabelle trug genau eine Zeile: die Auffangzeile `ohne`.
 *
 * ============================================================================
 * DER BAUM IST DIE LISTE
 * ============================================================================
 *
 * Mehrstufige Gruppen ohne sichtbare Stufen sind keine. Die Zeilen kommen in
 * der Reihenfolge des Pfades und werden nach ihrer Tiefe eingerückt — eine
 * flache alphabetische Liste stellte die Untergruppe von „Training" irgendwo
 * zwischen fremde Wurzeln.
 *
 * ============================================================================
 * WAS „LEER" HIER BEDEUTET
 * ============================================================================
 *
 * Ein leeres Feld heißt **erben**, nicht **nichts**. Deshalb steht neben
 * jedem vererbbaren Feld, was ohne eigene Angabe gelten würde — sonst sieht
 * man einer leeren Zeile nicht an, ob dort nichts gilt oder etwas geerbt
 * wird. Genau diesen Unterschied führt die Artikelakte als vier Zustände
 * (PRD-02), und er beginnt hier.
 *
 * ============================================================================
 * SEITENTYP LISTE (ÄA-41)
 * ============================================================================
 *
 * Der Zähler steht im Seitenkopf, „Warengruppe anlegen" in der Leiste über
 * der Tabelle, die Tabelle in einer Karte. Gepflegt wird weiter im Fenster:
 * Eine Gruppe hat neun Felder mit je einem Hinweis, was sie sonst erbte —
 * das passt nicht in eine Seitenleiste, und eine Vorschau, die beim
 * Überfahren der nächsten Zeile wechselte, verlöre einen halben Entwurf.
 */
export function Warengruppen({
  darfKonfigurieren,
}: {
  readonly darfKonfigurieren: boolean;
}): JSX.Element {
  const [gruppen, setzeGruppen] = useState<readonly Warengruppe[]>([]);
  const [schluessel, setzeSchluessel] = useState<readonly Steuerschluessel[]>([]);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [meldung, setzeMeldung] = useState('');

  /** Welche Gruppe im Fenster steht; `neu` heißt: eine neue. */
  const [offen, setzeOffen] = useState<Warengruppe | 'neu' | null>(null);
  const [formular, setzeFormular] = useState<Formular>(LEER);
  const [laeuft, setzeLaeuft] = useState(false);
  /** CRM-104: die Gruppe, deren Löschen im Pop-up nachfragt. */
  const [zuLoeschen, setzeZuLoeschen] = useState<Warengruppe | null>(null);

  const laden = useCallback(async (): Promise<void> => {
    setzeLaedt(true);
    try {
      const [alle, steuer] = await Promise.all([holeWarengruppen(), steuerschluesselAlle()]);
      setzeGruppen(alle);
      setzeSchluessel(steuer);
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaedt(false);
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  function oeffne(gruppe: Warengruppe | 'neu'): void {
    setzeMeldung('');
    setzeFehler(undefined);
    setzeOffen(gruppe);
    setzeFormular(gruppe === 'neu' ? LEER : ausGruppe(gruppe));
  }

  async function absenden(ereignis: React.FormEvent): Promise<void> {
    ereignis.preventDefault();
    if (offen === null) return;
    setzeLaeuft(true);
    setzeFehler(undefined);
    try {
      const felder: Warengruppenfelder = {
        bezeichnung: formular.bezeichnung.trim(),
        oberGruppe: leerAlsNull(formular.oberGruppe),
        steuerschluessel: leerAlsNull(formular.steuerschluessel),
        erloeskontoInland: leerAlsNull(formular.erloeskontoInland),
        erloeskontoEu: leerAlsNull(formular.erloeskontoEu),
        erloeskontoDrittland: leerAlsNull(formular.erloeskontoDrittland),
        standardtext: leerAlsNull(formular.standardtext),
        hoechstrabattProzent: leerAlsNull(formular.hoechstrabattProzent),
      };
      if (offen === 'neu') {
        await warengruppeAnlegen({ ...felder, schluessel: formular.schluessel.trim() });
        setzeMeldung(`Warengruppe ${formular.schluessel.trim()} angelegt.`);
      } else {
        await warengruppeAendern(offen.id, felder);
        setzeMeldung(`Warengruppe ${offen.schluessel} geändert.`);
      }
      setzeOffen(null);
      await laden();
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  }

  /** Wirft (CRM-104): Der Fehler steht in der Rückfrage, und sie bleibt offen. */
  async function loeschen(gruppe: Warengruppe): Promise<void> {
    setzeMeldung('');
    setzeFehler(undefined);
    await warengruppeLoeschen(gruppe.id);
    setzeZuLoeschen(null);
    setzeMeldung(`Warengruppe ${gruppe.schluessel} gelöscht.`);
    await laden();
  }

  /** Was ohne eigene Angabe gelten würde — der geerbte Wert (PRD-02). */
  function geerbt(gruppe: Warengruppe | 'neu' | null, feld: Vererbbar): string | null {
    const start =
      gruppe === null || gruppe === 'neu'
        ? leerAlsNull(formular.oberGruppe)
        : (leerAlsNull(formular.oberGruppe) ?? gruppe.oberGruppe);
    let bei = start;
    const gesehen = new Set<string>();
    while (bei !== null && !gesehen.has(bei)) {
      gesehen.add(bei);
      const eltern = gruppen.find((g) => g.id === bei);
      if (eltern === undefined) return null;
      if (eltern[feld] !== null) return eltern[feld];
      bei = eltern.oberGruppe;
    }
    return null;
  }

  /* ÄA-41: der Zähler im Seitenkopf — erst, wenn geladen ist. */
  useZaehler(laedt ? undefined : zaehlwort(gruppen.length, 'Warengruppe', 'Warengruppen'));

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}

      <Liste
        {...(darfKonfigurieren
          ? {
              filter: (
                <button className="knopf knopf--haupt" type="button" onClick={() => oeffne('neu')}>
                  Warengruppe anlegen
                </button>
              ),
            }
          : {})}
        hinweise={
          <>
            {/* PRD-02 */}
            <p className="hilfe">
              Steuerschlüssel, Erlöskonten, Standardtext und Höchstrabatt gelten für jede
              Untergruppe und jeden Artikel darunter, solange dort nichts Eigenes steht.
            </p>
            {meldung !== '' && (
              <p className="hinweiszeile" role="status">
                {meldung}
              </p>
            )}
          </>
        }
      >
        {laedt ? (
          <p role="status">Wird geladen …</p>
        ) : gruppen.length === 0 ? (
          <div className="liste__leer">
            <p className="hinweiszeile">
              Es ist noch keine Warengruppe angelegt. Ohne Gruppen erben Artikel weder
              Steuerschlüssel noch Erlöskonto, und die Navision-Zuordnung
              kennt nur die Auffangzeile „ohne".
            </p>
          </div>
        ) : (
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle" aria-label="Warengruppen">
              <thead>
                <tr>
                  <th scope="col">Gruppe</th>
                  <th scope="col">Schlüssel</th>
                  <th scope="col">Steuer</th>
                  <th scope="col">Erlöskonto Inland</th>
                  <th scope="col">Höchstrabatt</th>
                  <th scope="col">Hängt daran</th>
                  {darfKonfigurieren && <th scope="col">Handlung</th>}
                </tr>
              </thead>
              <tbody>
                {gruppen.map((g) => (
                  <tr key={g.id}>
                    <th scope="row">
                      {/*
                        DIE EINRÜCKUNG IST DIE STUFE (PRD-02). Sie steht als
                        Innenabstand und nicht als Leerzeichen im Text: Ein
                        Vorleseprogramm liest Leerzeichen nicht.
  
                        Die Stufe steht deshalb zusätzlich als vorgelesener Text —
                        NICHT als `aria-label` am selben Element. Das hätte die
                        Bezeichnung ERSETZT statt sie zu ergänzen, und die Gruppe
                        hieße für ein Vorleseprogramm nur noch „Stufe 2".
                      */}
                      <span style={{ paddingLeft: `${String(g.tiefe * 18)}px` }}>
                        {g.tiefe > 0 && <span aria-hidden="true">└ </span>}
                        {g.tiefe > 0 && (
                          <span className="nur-vorlesen">Stufe {g.tiefe + 1}: </span>
                        )}
                        {g.bezeichnung}
                      </span>
                    </th>
                    <td>
                      <code>{g.schluessel}</code>
                    </td>
                    <td>{g.steuerschluessel ?? <span className="hilfe">geerbt</span>}</td>
                    <td>{g.erloeskontoInland ?? <span className="hilfe">geerbt</span>}</td>
                    <td className="zahl">
                      {g.hoechstrabattProzent === null ? (
                        <span className="hilfe">—</span>
                      ) : (
                        `${g.hoechstrabattProzent} %`
                      )}
                    </td>
                    <td>
                      {/*
                        Woran die Gruppe hängt, steht VOR dem Löschen da — nicht
                        in der Fehlermeldung danach. Wer zwanzig Gruppen durchgeht,
                        will die freien sehen.
                      */}
                      {g.artikelzahl === 0 && g.untergruppen === 0 ? (
                        <span className="hilfe">nichts</span>
                      ) : (
                        [
                          g.artikelzahl > 0 ? `${String(g.artikelzahl)} Artikel` : '',
                          g.untergruppen > 0 ? `${String(g.untergruppen)} Untergruppen` : '',
                        ]
                          .filter((t) => t !== '')
                          .join(' · ')
                      )}
                    </td>
                    {darfKonfigurieren && (
                      <td>
                        <button
                          className="knopf knopf--klein"
                          type="button"
                          onClick={() => oeffne(g)}
                        >
                          Bearbeiten
                        </button>{' '}
                        <button
                          className="knopf knopf--eingriff knopf--klein"
                          type="button"
                          disabled={g.artikelzahl > 0 || g.untergruppen > 0}
                          onClick={() => {
                            setzeMeldung('');
                            setzeZuLoeschen(g);
                          }}
                        >
                          Löschen …
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Liste>

      {offen !== null && (
        <Dialog
          titel={offen === 'neu' ? 'Neue Warengruppe' : `Warengruppe ${offen.schluessel}`}
          unterzeile={offen === 'neu' ? undefined : offen.bezeichnung}
          breite="breit"
          beiSchliessen={() => setzeOffen(null)}
        >
          <form onSubmit={(e) => void absenden(e)}>
            {offen === 'neu' ? (
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="wgSchluessel">
                  Schlüssel <span className="feld__pflicht">Pflicht</span>
                </label>
                <input
                  id="wgSchluessel"
                  className="feld__eingabe"
                  value={formular.schluessel}
                  onChange={(e) => {
                    setzeFormular({ ...formular, schluessel: e.target.value.toUpperCase() });
                  }}
                  placeholder="TRAINING-PKW"
                />
                <p className="feld__hilfe">
                  Er wird vergeben, nicht aus der Bezeichnung errechnet — und er ist danach{' '}
                  <strong>nicht mehr änderbar</strong>: Auf ihm steht die Navision-Zuordnung.
                </p>
              </div>
            ) : (
              <div className="feld">
                <span className="feld__beschriftung">Schlüssel</span>
                <p>
                  <code>{offen.schluessel}</code>
                </p>
                <p className="feld__hilfe">
                  Nicht änderbar: Auf ihm steht die Zuordnung zur Navision-Produktbuchungsgruppe.
                  Wer einen anderen braucht, legt eine Gruppe an und hängt um.
                </p>
              </div>
            )}

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="wgBezeichnung">
                Bezeichnung <span className="feld__pflicht">Pflicht</span>
              </label>
              <input
                id="wgBezeichnung"
                className="feld__eingabe"
                value={formular.bezeichnung}
                onChange={(e) => {
                  setzeFormular({ ...formular, bezeichnung: e.target.value });
                }}
              />
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="wgOberGruppe">
                Obergruppe
              </label>
              <select
                id="wgOberGruppe"
                className="feld__eingabe"
                value={formular.oberGruppe}
                onChange={(e) => {
                  setzeFormular({ ...formular, oberGruppe: e.target.value });
                }}
              >
                <option value="">— keine, eine Wurzelgruppe —</option>
                {gruppen
                  /*
                    Die eigene Gruppe steht nicht zur Wahl. Ihre Untergruppen
                    stehen es doch: Der Kreis wird von der API abgewiesen und
                    mit Grund gemeldet (PRD-02) — eine Auswahl, die den halben
                    Baum verschweigt, erklärt niemandem, warum.
                  */
                  .filter((g) => offen === 'neu' || g.id !== offen.id)
                  .map((g) => (
                    <option key={g.id} value={g.id}>
                      {' '.repeat(g.tiefe * 2)}
                      {g.bezeichnung}
                    </option>
                  ))}
              </select>
            </div>

            <fieldset>
              <legend>Was an Artikel darunter vererbt wird</legend>

              <div className="feld">
                <label className="feld__beschriftung" htmlFor="wgSteuer">
                  Steuerschlüssel
                </label>
                <select
                  id="wgSteuer"
                  className="feld__eingabe"
                  value={formular.steuerschluessel}
                  onChange={(e) => {
                    setzeFormular({ ...formular, steuerschluessel: e.target.value });
                  }}
                >
                  <option value="">— erben —</option>
                  {schluessel.map((s) => (
                    <option key={s.schluessel} value={s.schluessel}>
                      {s.schluessel} · {s.bezeichnung}
                    </option>
                  ))}
                </select>
                <Geerbt wert={geerbt(offen, 'steuerschluessel')} />
              </div>

              {/* PRD-04: getrennt nach Inland, EU und Drittland. */}
              {(
                [
                  ['erloeskontoInland', 'Erlöskonto Inland'],
                  ['erloeskontoEu', 'Erlöskonto EU'],
                  ['erloeskontoDrittland', 'Erlöskonto Drittland'],
                ] as const
              ).map(([feld, beschriftung]) => (
                <div className="feld" key={feld}>
                  <label className="feld__beschriftung" htmlFor={`wg-${feld}`}>
                    {beschriftung}
                  </label>
                  <input
                    id={`wg-${feld}`}
                    className="feld__eingabe"
                    value={formular[feld]}
                    onChange={(e) => {
                      setzeFormular({ ...formular, [feld]: e.target.value });
                    }}
                  />
                  <Geerbt wert={geerbt(offen, feld)} />
                </div>
              ))}

              <div className="feld">
                <label className="feld__beschriftung" htmlFor="wgHoechstrabatt">
                  Höchstrabatt in Prozent
                </label>
                <input
                  id="wgHoechstrabatt"
                  className="feld__eingabe"
                  value={formular.hoechstrabattProzent}
                  onChange={(e) => {
                    setzeFormular({ ...formular, hoechstrabattProzent: e.target.value });
                  }}
                  placeholder="10.00"
                  inputMode="decimal"
                />
                {/* PRF-11 */}
                <p className="feld__hilfe">
                  Wird er überschritten, wird auf den Höchstwert gekappt und die
                  Kappung in der Preisherleitung ausgewiesen. Punkt als Dezimaltrennzeichen.
                </p>
                <Geerbt wert={geerbt(offen, 'hoechstrabattProzent')} />
              </div>

              <div className="feld">
                <label className="feld__beschriftung" htmlFor="wgStandardtext">
                  Standardtext
                </label>
                <textarea
                  id="wgStandardtext"
                  className="feld__eingabe"
                  rows={3}
                  value={formular.standardtext}
                  onChange={(e) => {
                    setzeFormular({ ...formular, standardtext: e.target.value });
                  }}
                />
                <Geerbt wert={geerbt(offen, 'standardtext')} />
              </div>
            </fieldset>

            <div className="feldreihe">
              <button className="knopf knopf--haupt" type="submit" disabled={laeuft}>
                {offen === 'neu' ? 'Anlegen' : 'Speichern'}
              </button>{' '}
              <button
                className="knopf knopf--verweis"
                type="button"
                onClick={() => setzeOffen(null)}
              >
                Abbrechen
              </button>
            </div>
          </form>
        </Dialog>
      )}

      {zuLoeschen !== null && (
        <Rueckfrage
          frage="Warengruppe löschen?"
          unterzeile={`${zuLoeschen.schluessel} · ${zuLoeschen.bezeichnung}`}
          tat="Löschen"
          beiBestaetigen={() => loeschen(zuLoeschen)}
          beiSchliessen={() => setzeZuLoeschen(null)}
        >
          <p>
            An der Gruppe hängen weder Artikel noch Untergruppen. Sie steht danach weder als
            Obergruppe noch am Artikel zur Wahl; zurückholen lässt sie sich hier nicht.
          </p>
        </Rueckfrage>
      )}
    </>
  );
}

/** Die vererbbaren Felder — für die Anzeige „was gälte ohne eigene Angabe". */
type Vererbbar =
  | 'steuerschluessel'
  | 'erloeskontoInland'
  | 'erloeskontoEu'
  | 'erloeskontoDrittland'
  | 'standardtext'
  | 'hoechstrabattProzent';

/**
 * Was ohne eigene Angabe gelten würde.
 *
 * Ohne diese Zeile sieht man einem leeren Feld nicht an, ob dort nichts gilt
 * oder etwas geerbt wird — genau der Unterschied, den die Artikelakte als
 * vier Zustände führt (PRD-02).
 */
function Geerbt({ wert }: { readonly wert: string | null }): JSX.Element | null {
  if (wert === null) return null;
  return <p className="feld__hilfe">Ohne eigene Angabe gilt: {wert}</p>;
}

/** Das Formular führt Text, auch für Zahlen: Ein leeres Feld ist kein 0. */
type Formular = {
  schluessel: string;
  bezeichnung: string;
  oberGruppe: string;
  steuerschluessel: string;
  erloeskontoInland: string;
  erloeskontoEu: string;
  erloeskontoDrittland: string;
  standardtext: string;
  hoechstrabattProzent: string;
};

const LEER: Formular = {
  schluessel: '',
  bezeichnung: '',
  oberGruppe: '',
  steuerschluessel: '',
  erloeskontoInland: '',
  erloeskontoEu: '',
  erloeskontoDrittland: '',
  standardtext: '',
  hoechstrabattProzent: '',
};

function ausGruppe(g: Warengruppe): Formular {
  return {
    schluessel: g.schluessel,
    bezeichnung: g.bezeichnung,
    oberGruppe: g.oberGruppe ?? '',
    steuerschluessel: g.steuerschluessel ?? '',
    erloeskontoInland: g.erloeskontoInland ?? '',
    erloeskontoEu: g.erloeskontoEu ?? '',
    erloeskontoDrittland: g.erloeskontoDrittland ?? '',
    standardtext: g.standardtext ?? '',
    hoechstrabattProzent: g.hoechstrabattProzent ?? '',
  };
}

/** Leer heißt ERBEN und nicht „leerer Text" (PRD-02). */
function leerAlsNull(wert: string): string | null {
  const geputzt = wert.trim();
  return geputzt === '' ? null : geputzt;
}
